import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
    IPaginatedApiResponse,
    IApiResponse,
} from "../../../../utils/types/api-response";
import {
    getStatementOfAccount,
    exportStatementOfAccount,
    downloadStatementOfAccount,
    fetchProviderSessionActivityReport,
    exportProviderSessionActivityReport,
    getTerminatedClients,
    exportTerminatedClients,
    getConfirmationReports,
    getNoteAuditReport,
    getProviderNoteAuditReport,
    getAgingReport,
    exportAgingReport,
    getConsentReports,
    getCollateralConsentReports,
    getClientActiveSessions,
    exportConsentReports,
    getConsentsNeedingRenewal,
    getExpiringConsents,
    exportExpiringConsents,
    fetchProviderAccommodations,
} from "../../services/reports.services";
import {
    IAgingReportResponse,
    ISessionActivityFilters,
    ISessionActivityResponse,
    IStatementOfAccountFilters,
    IExportStatementOfAccountFilters,
    IStatementOfAccountPreviewResponse,
    ITerminatedClient,
    ITerminatedClientsFilters,
    IConfirmationReportFilters,
    IConfirmationReport,
    INoteAuditReportFilters,
    INoteAuditReport,
    IProviderNoteAuditReport,
    IAgingReportFilters,
    IAgingReport,
    IConsentReportFilters,
    IConsentReport,
    ICollateralConsentReport,
    ICollateralConsentReportFilters,
    IClientActiveSession,
    IConsentsNeedingRenewalResponse,
    IExpiringConsent,
    IProviderAccommodation,
    IProviderAccommodationFilters,
} from "../../types/reports.types";
import { handleRemoveEmptyParamFromQueryParams } from "../../../../utils/helpers";
import { useDoesUserHavePermission } from "../../../../hooks/permissions";
import { UserPermisions } from "../../../../utils/types/user";
import { IBillingAdminDashboardFilters } from "../../../../utils/types/billing";
import { fetchBillingAdminDashboardList } from "../../../../services/billing";

export function useFetchStatementOfAccount(
    filters: IStatementOfAccountFilters,
    isEnabled: boolean
) {
    return useQuery<
        IPaginatedApiResponse<IStatementOfAccountPreviewResponse[]>,
        AxiosError<IPaginatedApiResponse<IStatementOfAccountPreviewResponse[]>>
    >({
        queryKey: ["statement-of-account", filters],
        queryFn: () => getStatementOfAccount(filters),
        enabled: isEnabled,
    });
}

export function useGetDownloadJobId(
    filters: IStatementOfAccountFilters,
    isEnabled: boolean
) {
    interface IDownloadResponseData {
        export_job_id: string;
        expected_pdf_count: number;
    }
    return useQuery<
        IApiResponse<IDownloadResponseData>,
        AxiosError<IApiResponse<IDownloadResponseData>>
    >({
        queryKey: ["statement-of-account-download-jobId", filters],
        queryFn: () => getStatementOfAccount(filters),
        enabled: isEnabled,
    });
}

export function useExportStatementOfAccount(
    filters: IExportStatementOfAccountFilters,
    fileName: string
) {
    return useQuery<Blob, AxiosError<Blob>>({
        queryKey: ["export-statement-of-account", filters],
        queryFn: () => exportStatementOfAccount(filters),
        onSuccess: (data) => {
            const href = window.URL.createObjectURL(data);

            const anchorElement = document.createElement("a");
            anchorElement.href = href;
            anchorElement.download = fileName;

            document.body.appendChild(anchorElement);
            anchorElement.click();

            document.body.removeChild(anchorElement);
            window.setTimeout(() => window.URL.revokeObjectURL(href), 0);
        },
        enabled: false,
    });
}

export function useDownloadStatementOfAccount(
    pdfType: string,
    jobId: string,
    isEnabled: boolean
) {
    return useQuery<void, AxiosError<string>>({
        queryKey: ["download-statement-of-account", jobId],
        queryFn: () => downloadStatementOfAccount(pdfType, jobId),
        enabled: isEnabled,
    });
}

export function useFetchProviderSessionActivityReport(
    filters: ISessionActivityFilters
) {
    return useQuery<
        IApiResponse<ISessionActivityResponse>,
        AxiosError<IApiResponse<ISessionActivityResponse[]>>
    >({
        queryKey: ["provider-session-activity", filters],
        queryFn: () =>
            fetchProviderSessionActivityReport(
                handleRemoveEmptyParamFromQueryParams(filters)
            ),
    });
}

export function useExportProviderSessionActivityReport(
    filters: ISessionActivityFilters,
    isEnabled: boolean
) {
    return useQuery<Blob, AxiosError<string>>({
        queryKey: [
            "provider-session-activity-report",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => exportProviderSessionActivityReport(filters),
        enabled: isEnabled,
        onSuccess: (data) => {
            const href = window.URL.createObjectURL(data);

            const anchorElement = document.createElement("a");
            anchorElement.href = href;

            document.body.appendChild(anchorElement);
            anchorElement.click();

            document.body.removeChild(anchorElement);
            window.URL.revokeObjectURL(href);
        },
    });
}

export function useFetchAgingReport(
    filters: IBillingAdminDashboardFilters,
    enabled: boolean
) {
    return useQuery<
        IPaginatedApiResponse<IAgingReportResponse[]>,
        AxiosError<IPaginatedApiResponse<IAgingReportResponse[]>>
    >({
        queryKey: [
            "billing-aging-report",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => fetchBillingAdminDashboardList(filters),
        enabled,
    });
}

export function useGetTerminatedClients(filters: ITerminatedClientsFilters) {
    return useQuery<
        IPaginatedApiResponse<ITerminatedClient[]>,
        AxiosError<IPaginatedApiResponse<ITerminatedClient[]>>
    >({
        queryKey: [
            "terminated-clients",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () =>
            getTerminatedClients(
                handleRemoveEmptyParamFromQueryParams(filters)
            ),
    });
}

export function useExportTerminatedClients(
    filters: ITerminatedClientsFilters,
    isEnabled: boolean
) {
    return useQuery<Blob, AxiosError<string>>({
        queryKey: [
            "export-terminated-clients",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () =>
            exportTerminatedClients(
                handleRemoveEmptyParamFromQueryParams(filters)
            ),
        enabled: isEnabled,
        onSuccess: (data) => {
            const href = window.URL.createObjectURL(data);

            const anchorElement = document.createElement("a");
            anchorElement.href = href;

            document.body.appendChild(anchorElement);
            anchorElement.click();

            document.body.removeChild(anchorElement);
            window.URL.revokeObjectURL(href);
        },
    });
}

export function useGetConfirmationReports(filters: IConfirmationReportFilters) {
    return useQuery<
        IPaginatedApiResponse<IConfirmationReport[]>,
        AxiosError<IPaginatedApiResponse<IConfirmationReport[]>>
    >({
        queryKey: [
            "confirmation-reports",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => getConfirmationReports(filters),
    });
}

export function useGetNoteAuditReport(filters: INoteAuditReportFilters) {
    return useQuery<
        IApiResponse<INoteAuditReport>,
        AxiosError<IApiResponse<INoteAuditReport>>
    >({
        queryKey: [
            "note-audit-report",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => getNoteAuditReport(filters),
    });
}

export function useGetProviderNoteAuditReport(
    providerId: string,
    filters: INoteAuditReportFilters,
    enabled?: boolean
) {
    return useQuery<
        IApiResponse<IProviderNoteAuditReport>,
        AxiosError<IApiResponse<IProviderNoteAuditReport>>
    >({
        queryKey: [
            "provider-note-audit-report",
            providerId,
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => getProviderNoteAuditReport(providerId, filters),
        enabled,
    });
}

export function useGetAgingReport(filters: IAgingReportFilters) {
    return useQuery<
        IPaginatedApiResponse<IAgingReport[]>,
        AxiosError<IPaginatedApiResponse<IAgingReport[]>>
    >({
        queryKey: [
            "aging-report",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => getAgingReport(filters),
    });
}

export function useExportAgingReport(
    filters: IAgingReportFilters,
    enabled: boolean
) {
    return useQuery<Blob, AxiosError<string>>({
        queryKey: [
            "export-aging-report",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => exportAgingReport(filters),
        onSuccess: (data) => {
            const href = window.URL.createObjectURL(data);

            const anchorElement = document.createElement("a");
            anchorElement.href = href;

            document.body.appendChild(anchorElement);
            anchorElement.click();

            document.body.removeChild(anchorElement);
            window.URL.revokeObjectURL(href);
        },
        enabled,
    });
}

// Consent Reports Query Hooks
export function useGetConsentReports(filters: IConsentReportFilters) {
    return useQuery<
        IPaginatedApiResponse<IConsentReport[]>,
        AxiosError<IPaginatedApiResponse<IConsentReport[]>>
    >({
        queryKey: [
            "consent-reports",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => getConsentReports(filters),
    });
}

export function useGetCollateralConsentReports(
    filters: ICollateralConsentReportFilters
) {
    return useQuery<
        IPaginatedApiResponse<ICollateralConsentReport[]>,
        AxiosError<IPaginatedApiResponse<ICollateralConsentReport[]>>
    >({
        queryKey: [
            "collateral-consent-reports",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => getCollateralConsentReports(filters),
    });
}

export function useGetClientActiveSessions(clientId: string, enabled = true) {
    return useQuery<
        IApiResponse<IClientActiveSession[]>,
        AxiosError<IApiResponse<IClientActiveSession[]>>
    >({
        queryKey: ["client-active-sessions", clientId],
        queryFn: () => getClientActiveSessions(clientId),
        enabled: enabled && !!clientId,
    });
}

export function useExportConsentReports(
    filters: IConsentReportFilters,
    enabled: boolean
) {
    return useQuery({
        queryKey: ["export-consent-reports", filters],
        queryFn: async () => {
            const data = await exportConsentReports(filters);

            const href = window.URL.createObjectURL(data);

            const anchorElement = document.createElement("a");
            anchorElement.href = href;
            anchorElement.download = `consent-reports-${
                new Date().toISOString().split("T")[0]
            }.csv`;

            document.body.appendChild(anchorElement);
            anchorElement.click();

            document.body.removeChild(anchorElement);
            window.URL.revokeObjectURL(href);
        },
        enabled,
    });
}

export function useGetConsentsNeedingRenewal(filters: IConsentReportFilters) {
    return useQuery<
        IConsentsNeedingRenewalResponse,
        AxiosError<IConsentsNeedingRenewalResponse>
    >({
        queryKey: [
            "consents-needing-renewal",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => getConsentsNeedingRenewal(filters),
    });
}

export function useGetExpiringConsents(filters: IConsentReportFilters) {
    return useQuery<
        IPaginatedApiResponse<IExpiringConsent[]>,
        AxiosError<IPaginatedApiResponse<IExpiringConsent[]>>
    >({
        queryKey: [
            "expiring-consents",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => getExpiringConsents(filters),
    });
}

export function useExportExpiringConsents(
    filters: IConsentReportFilters,
    enabled: boolean
) {
    return useQuery({
        queryKey: [
            "export-expiring-consents",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: async () => {
            const data = await exportExpiringConsents(
                handleRemoveEmptyParamFromQueryParams(filters)
            );

            const href = window.URL.createObjectURL(data);

            const anchorElement = document.createElement("a");
            anchorElement.href = href;
            anchorElement.download = `expiring-consents-${
                new Date().toISOString().split("T")[0]
            }.csv`;

            document.body.appendChild(anchorElement);
            anchorElement.click();

            document.body.removeChild(anchorElement);
            window.URL.revokeObjectURL(href);
        },
        enabled,
    });
}

export function useFetchProviderAccommodations(
    filters: IProviderAccommodationFilters
) {
    const canViewProviderAccommodations = useDoesUserHavePermission([
        UserPermisions.PROVIDER_ACCOMMODATION_READ,
    ]);

    return useQuery<
        IPaginatedApiResponse<IProviderAccommodation[]>,
        AxiosError<IPaginatedApiResponse<IProviderAccommodation[]>>
    >({
        queryKey: [
            "provider-accommodations",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => fetchProviderAccommodations(filters),
        enabled: canViewProviderAccommodations,
    });
}
